import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCalendarAlt, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { Colors } from '../../../styles/colors';

// Styled components
const CurrentHoursListContainer = styled.div`
  ${tw`divide-y divide-border-default`}
`;

const DayRow = styled.div<{ available: boolean }>`
  ${tw`p-4 flex justify-between items-center`}
  ${({ available }) => !available && tw`text-text-tertiary`}
`;

const DayLabel = styled.div`
  ${tw`font-medium`}
`;

const DayHours = styled.div`
  ${tw`text-sm text-text-secondary flex items-center`}
`;

const NoHoursContainer = styled.div`
  ${tw`p-8 text-center bg-bg-secondary`}
`;

const NoHoursMessage = styled.p`
  ${tw`text-text-secondary mb-4 flex flex-col items-center justify-center`}
`;

const CalendarIcon = styled(FontAwesomeIcon)`
  ${tw`text-text-tertiary text-4xl mb-4`}
`;

const ClockIcon = styled(FontAwesomeIcon)`
  ${tw`mr-2 mb-3`}
  color: ${Colors.Green800};
`;

const WarningIcon = styled(FontAwesomeIcon)`
  ${tw`mr-2 text-yellow-500`}
`;

const AlwaysAvailableTitle = styled.span`
  ${tw`font-bold text-xl`}
  color: ${Colors.Green800};
`;

const AlwaysAvailableDescription = styled.span`
  ${tw`text-base mt-2`}
  color: ${Colors.Green800};
`;

// Helper function
const formatTimeDisplay = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  return `${hour % 12 || 12}:${minutes} ${hour < 12 ? 'AM' : 'PM'}`;
};

interface Day {
  id: string;
  label: string;
}

interface CurrentHoursListProps {
  hours: any;
  days: Day[];
}

const CurrentHoursList: React.FC<CurrentHoursListProps> = ({ hours, days }) => {
  if (!hours) {
    return (
      <NoHoursContainer>
        <NoHoursMessage>
          <CalendarIcon icon={faCalendarAlt} />
          <span>No operating hours have been set up yet.</span>
          <span className="text-sm mt-2">
            Click the button below to configure your operating hours.
          </span>
        </NoHoursMessage>
      </NoHoursContainer>
    );
  }

  if (hours.alwaysAvailable) {
    return (
      <NoHoursContainer>
        <NoHoursMessage>
          <ClockIcon icon={faClock} size="2x" className="mb-4" />
          <AlwaysAvailableTitle>Always Available</AlwaysAvailableTitle>
          <AlwaysAvailableDescription>
            Your location is set to be available 24/7.
          </AlwaysAvailableDescription>
        </NoHoursMessage>
      </NoHoursContainer>
    );
  }

  return (
    <CurrentHoursListContainer>
      {days.map(day => {
        // Use type assertion to safely access properties
        const available = hours[`${day.id}Available` as keyof typeof hours] as boolean;
        const alwaysAvailable = hours[`${day.id}AlwaysAvailable` as keyof typeof hours] as boolean;
        const start = hours[`${day.id}Start` as keyof typeof hours] as string;
        const end = hours[`${day.id}End` as keyof typeof hours] as string;

        return (
          <DayRow key={day.id} available={!!available}>
            <DayLabel>{day.label}</DayLabel>
            <DayHours>
              {!available && (
                <>
                  <WarningIcon icon={faExclamationCircle} />
                  Not Available
                </>
              )}
              {available && alwaysAvailable && (
                <>
                  <ClockIcon icon={faClock} />
                  Always Available
                </>
              )}
              {available && !alwaysAvailable && start && end && (
                <>
                  <ClockIcon icon={faClock} />
                  {formatTimeDisplay(start)} - {formatTimeDisplay(end)}
                </>
              )}
            </DayHours>
          </DayRow>
        );
      })}
    </CurrentHoursListContainer>
  );
};

export default CurrentHoursList;
